import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { Box, Container, Button, CircularProgress, Typography, Link, Tooltip, IconButton } from '@mui/material';
import { useJsApiLoader, GoogleMap } from '@react-google-maps/api';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import { theme } from '../theme/colors';
import EventsFiltersToolbar from '../components/EventsComponents/EventsFiltersToolbar';
import { fetchEventTypes } from '../utils/eventApi';
import { useEventsFilters } from '../hooks/useEventsFilters';
import { useEventsLocationSearch } from '../hooks/useEventsLocationSearch';
import { useUser } from '../context/UserContext';
import {
  getGooglePlacesApiKey, GOOGLE_MAP_ID, GOOGLE_MAPS_LIBRARIES, US_CENTER, US_DEFAULT_ZOOM,
} from '../utils/googleMapsConfig';
import { createEventMarker } from '../components/EventsComponents/EventMapMarker';
import { isExternalEvent, getExternalUrl } from '../utils/externalEvents';
import { formatNearZipLocationLabel } from '../utils/eventProximity';
import '../styles/component_styles/Events.scss';

const GOOGLE_MAPS_API_KEY = getGooglePlacesApiKey();

// Pin ceiling for one draw. Advanced Markers are real DOM, so this is a frame-rate
// guard rather than a data limit — narrowing the viewport is what surfaces the rest.
const MAP_MAX_MARKERS = 150;
// The map queries a viewport, so it can afford a wider net than the paginated list.
const MAP_MAX_EVENTS = 250;
const MAP_NATIVE_PAGE_SIZE = 200;

// How far the viewport must move before offering a fresh search: a quarter of a
// screen of panning, or roughly a full zoom step.
const BOUNDS_PAN_RATIO = 0.25;
const BOUNDS_ZOOM_RATIO = 0.6;
// Window in which camera movement is attributed to us rather than the user.
const PROGRAMMATIC_MOVE_MS = 1200;

function eventCoords(event) {
  const lat = typeof event.latitude === 'number' ? event.latitude : parseFloat(event.latitude);
  const lng = typeof event.longitude === 'number' ? event.longitude : parseFloat(event.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

function hasCoords(event) {
  return eventCoords(event) !== null;
}

function toPlainBounds(latLngBounds) {
  if (!latLngBounds) return null;
  const json = latLngBounds.toJSON();
  return {
    north: json.north, south: json.south, east: json.east, west: json.west,
  };
}

/** Longitude width, accounting for a viewport that crosses the antimeridian. */
function lngSpan(bounds) {
  const raw = bounds.east - bounds.west;
  return raw >= 0 ? raw : raw + 360;
}

function lngCenter(bounds) {
  const center = bounds.west + lngSpan(bounds) / 2;
  return center > 180 ? center - 360 : center;
}

/**
 * Whether the viewport has moved far enough from the last searched area that
 * re-querying would plausibly return different events.
 */
function boundsDrifted(current, searched) {
  if (!current) return false;
  if (!searched) return true;

  const latSpan = current.north - current.south;
  const currentLngSpan = lngSpan(current);
  if (latSpan <= 0 || currentLngSpan <= 0) return true;

  const latShift = Math.abs(
    (current.north + current.south) / 2 - (searched.north + searched.south) / 2,
  );
  if (latShift > latSpan * BOUNDS_PAN_RATIO) return true;

  let lngShift = Math.abs(lngCenter(current) - lngCenter(searched));
  if (lngShift > 180) lngShift = 360 - lngShift;
  if (lngShift > currentLngSpan * BOUNDS_PAN_RATIO) return true;

  const searchedLatSpan = searched.north - searched.south;
  if (searchedLatSpan <= 0) return true;
  const zoomRatio = latSpan / searchedLatSpan;
  return zoomRatio < BOUNDS_ZOOM_RATIO || zoomRatio > 1 / BOUNDS_ZOOM_RATIO;
}

export default function EventsMapPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, userState, canCreateEvent, accountTypeName } = useUser();
  const isLoggedIn = !!user;
  const organizerId = searchParams.get('organizer') || '';
  const nearZipParam = searchParams.get('near_zip') || '';

  const initialFilters = useMemo(() => {
    const nearZip = nearZipParam.trim().slice(0, 5);
    const hasNearZip = /^\d{5}$/.test(nearZip);
    const locationFromUrl = searchParams.get('location_search') || '';
    return {
      event_type: searchParams.get('event_type') || '',
      locationSearch: hasNearZip
        ? formatNearZipLocationLabel(nearZip, searchParams.get('state') || userState || '')
        : locationFromUrl,
      rsvp: searchParams.get('rsvp') || '',
      civix_only: searchParams.get('civix_only') || '',
      nearZip: hasNearZip ? nearZip : '',
    };
  }, [searchParams, nearZipParam, userState]);

  const showHostEventButton = Boolean(
    isLoggedIn && (canCreateEvent || accountTypeName)
  );

  // The area the results currently describe. Only set by "Search this area", so
  // panning the map never kicks off a fetch on its own.
  const [searchBounds, setSearchBounds] = useState(null);

  const {
    events, loading, loadingMore, externalPartial, error,
    selectedEventType, setSelectedEventType,
    locationSearchInput, setLocationSearchInput,
    submittedLocationSearch, setSubmittedLocationSearch,
    useCurrentLocation, setUseCurrentLocation,
    rsvpFilter, setRsvpFilter,
    civixOnly, setCivixOnly,
    loadEvents, getFilterQueryParams,
  } = useEventsFilters({
    organizerId,
    isLoggedIn,
    initialFilters,
    bounds: searchBounds,
    maxEvents: MAP_MAX_EVENTS,
    pageSize: MAP_NATIVE_PAGE_SIZE,
    nearZip: initialFilters.nearZip,
  });

  const [eventTypes, setEventTypes] = useState([]);
  const [searchRegion, setSearchRegion] = useState(null);
  const [map, setMap] = useState(null);
  const [viewportBounds, setViewportBounds] = useState(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  const markersRef = useRef(new Map());
  const hasFramedRef = useRef(false);
  const programmaticUntilRef = useRef(Date.now() + PROGRAMMATIC_MOVE_MS);

  const markProgrammaticMove = useCallback(() => {
    programmaticUntilRef.current = Date.now() + PROGRAMMATIC_MOVE_MS;
  }, []);

  const locationSearch = useEventsLocationSearch({
    isLoggedIn,
    userState,
    locationSearchInput,
    setLocationSearchInput,
    submittedLocationSearch,
    setSubmittedLocationSearch,
    useCurrentLocation,
    setUseCurrentLocation,
    loadEvents,
  });

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
    mapIds: [GOOGLE_MAP_ID],
  });

  useEffect(() => {
    fetchEventTypes().then(setEventTypes).catch(() => {});
  }, []);

  const allMappable = useMemo(() => events.filter(hasCoords), [events]);
  const mappableEvents = useMemo(
    () => allMappable.slice(0, MAP_MAX_MARKERS),
    [allMappable],
  );
  const hiddenCount = events.length - allMappable.length;
  const mapMarkerOverflow = allMappable.length - mappableEvents.length;

  const filterQs = getFilterQueryParams().toString();
  const listHref = filterQs ? `/events?${filterQs}` : '/events';
  const mapHref = filterQs ? `/events/map?${filterQs}` : '/events/map';

  const showSearchArea = useMemo(
    () => hasInteracted && boundsDrifted(viewportBounds, searchBounds),
    [hasInteracted, viewportBounds, searchBounds],
  );

  // External events have no CivixThread detail page, so their pins open the
  // host site in a new tab instead of navigating to a route that doesn't exist.
  const handleSelectEvent = useCallback((event) => {
    const externalUrl = getExternalUrl(event);
    if (isExternalEvent(event) && externalUrl) {
      window.open(externalUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(`/events/${event.id}`);
  }, [navigate]);

  // Markers are created once and reused, so the handler is read through a ref
  // rather than being rebound (which would mean tearing every pin back down).
  const selectRef = useRef(handleSelectEvent);
  useEffect(() => { selectRef.current = handleSelectEvent; }, [handleSelectEvent]);

  const onMapLoad = useCallback((instance) => {
    // The map's own initial framing is not the user asking for a new area.
    markProgrammaticMove();
    setMap(instance);
  }, [markProgrammaticMove]);

  const onMapUnmount = useCallback(() => {
    markersRef.current.forEach((marker) => { marker.map = null; });
    markersRef.current = new Map();
    setMap(null);
  }, []);

  useEffect(() => () => {
    markersRef.current.forEach((marker) => { marker.map = null; });
    markersRef.current = new Map();
  }, []);

  const handleIdle = useCallback(() => {
    if (!map) return;
    const bounds = toPlainBounds(map.getBounds());
    if (bounds) setViewportBounds(bounds);
  }, [map]);

  useEffect(() => {
    if (!map) return undefined;
    const listeners = [
      map.addListener('idle', handleIdle),
      // A drag is unambiguously the user. Zoom is not — fitBounds zooms too —
      // so it only counts outside the window we opened for our own camera work.
      map.addListener('dragend', () => setHasInteracted(true)),
      map.addListener('zoom_changed', () => {
        if (Date.now() >= programmaticUntilRef.current) setHasInteracted(true);
      }),
    ];
    return () => listeners.forEach((listener) => listener.remove());
  }, [map, handleIdle]);

  const handleSearchThisArea = useCallback(() => {
    if (!viewportBounds) return;
    setSearchBounds(viewportBounds);
  }, [viewportBounds]);

  const handleClearArea = useCallback(() => {
    setSearchBounds(null);
  }, []);

  // Geocode the submitted location search to a region viewport so the map can
  // zoom to the searched city/state even when no events fall inside it.
  useEffect(() => {
    if (!isLoaded) return undefined;
    const query = (submittedLocationSearch || '').trim();
    if (!query) {
      setSearchRegion(null);
      return undefined;
    }
    let cancelled = false;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode(
      { address: query, componentRestrictions: { country: 'us' } },
      (results, status) => {
        if (cancelled) return;
        if (status === 'OK' && results && results[0]) {
          const geo = results[0].geometry;
          setSearchRegion(geo.viewport || geo.bounds || null);
        } else {
          setSearchRegion(null);
        }
      },
    );
    return () => { cancelled = true; };
  }, [isLoaded, submittedLocationSearch]);

  // Reconcile pins against the current results, reusing the markers that survive
  // so an incoming batch of Mobilize events doesn't rebuild every SVG on screen.
  useEffect(() => {
    if (!map) return;
    const next = new Map();
    mappableEvents.forEach((event) => {
      const key = String(event.id);
      const existing = markersRef.current.get(key);
      if (existing) {
        markersRef.current.delete(key);
        next.set(key, existing);
        return;
      }
      const marker = createEventMarker({
        map,
        event,
        onSelect: (selected) => selectRef.current(selected),
      });
      if (marker) next.set(key, marker);
    });
    markersRef.current.forEach((marker) => { marker.map = null; });
    markersRef.current = next;
  }, [map, mappableEvents]);

  // A submitted location search is explicit intent, so it always wins the camera.
  useEffect(() => {
    if (!map || !searchRegion) return;
    const bounds = new window.google.maps.LatLngBounds();
    bounds.union(searchRegion);
    markProgrammaticMove();
    map.fitBounds(bounds, 64);
    hasFramedRef.current = true;
  }, [map, searchRegion, markProgrammaticMove]);

  // Frame the first batch of results once. Afterwards the viewport belongs to
  // the user — Mobilize events arriving late must not yank the camera back.
  useEffect(() => {
    if (!map || searchRegion || hasFramedRef.current) return;
    if (hasInteracted || searchBounds) return;
    if (mappableEvents.length === 0) return;

    const bounds = new window.google.maps.LatLngBounds();
    mappableEvents.forEach((event) => {
      const coords = eventCoords(event);
      if (coords) bounds.extend(coords);
    });

    markProgrammaticMove();
    if (mappableEvents.length === 1) {
      map.setCenter(bounds.getCenter());
      map.setZoom(12);
    } else {
      map.fitBounds(bounds, 64);
    }
    hasFramedRef.current = true;
  }, [map, mappableEvents, searchRegion, hasInteracted, searchBounds, markProgrammaticMove]);

  const handleClearAllFilters = useCallback(() => {
    setSelectedEventType('');
    setRsvpFilter('');
    setCivixOnly(false);
    setSearchBounds(null);
    locationSearch.handleClearSearch();
  }, [setSelectedEventType, setRsvpFilter, setCivixOnly, locationSearch]);

  const handleHostEvent = () => {
    if (!isLoggedIn) {
      navigate('/auth/login');
      return;
    }
    navigate('/events/create');
  };

  const busy = loading || loadingMore;
  const showStatusRow = !loading && !error
    && (hiddenCount > 0 || mapMarkerOverflow > 0 || Boolean(searchBounds));

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', p: 2 }}>
      <Container maxWidth="lg" sx={{ px: 0 }}>
        <EventsFiltersToolbar
          viewMode="map"
          listHref={listHref}
          mapHref={mapHref}
          eventTypes={eventTypes}
          selectedEventType={selectedEventType}
          onEventTypeChange={setSelectedEventType}
          isLoggedIn={isLoggedIn}
          rsvpFilter={rsvpFilter}
          onRsvpFilterChange={setRsvpFilter}
          civixOnly={civixOnly}
          onCivixOnlyChange={setCivixOnly}
          showHostEventButton={showHostEventButton}
          onHostEvent={handleHostEvent}
          locationSearchInput={locationSearchInput}
          setLocationSearchInput={setLocationSearchInput}
          submittedLocationSearch={submittedLocationSearch}
          setSubmittedLocationSearch={setSubmittedLocationSearch}
          setUseCurrentLocation={setUseCurrentLocation}
          onClearAllFilters={handleClearAllFilters}
          sx={{ mb: 2 }}
          {...locationSearch}
        />

        {showStatusRow && (
          <Box sx={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1,
            mb: 1.5, px: 0.5, fontSize: 13, color: '#475569',
          }}>
            {searchBounds && (
              <Typography component="span" sx={{ fontSize: 13, color: '#334155', fontWeight: 600 }}>
                Showing this area ·{' '}
                <Link
                  component="button"
                  type="button"
                  onClick={handleClearArea}
                  sx={{ fontSize: 13, fontWeight: 700, color: theme.primary }}
                >
                  Show everywhere
                </Link>
              </Typography>
            )}

            {mapMarkerOverflow > 0 && (
              <Typography component="span" sx={{ fontSize: 13, color: '#475569' }}>
                Showing {MAP_MAX_MARKERS} of {allMappable.length} pins — zoom in and search this area for the rest
              </Typography>
            )}

            {hiddenCount > 0 && (
              <Typography component="span" sx={{ fontSize: 13, color: '#475569' }}>
                {hiddenCount} {hiddenCount === 1 ? 'event has' : 'events have'} no map location (online or unlisted address) ·{' '}
                <Link
                  component={RouterLink}
                  to={listHref}
                  sx={{ fontSize: 13, fontWeight: 700, color: theme.primary }}
                >
                  View in list
                </Link>
              </Typography>
            )}
          </Box>
        )}

        <Box sx={{
          position: 'relative', borderRadius: '14px', overflow: 'hidden',
          border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          height: { xs: 'calc(100vh - 280px)', md: 'calc(100vh - 260px)' }, minHeight: 420, bgcolor: '#e5e7eb',
        }}>
          {!GOOGLE_MAPS_API_KEY ? (
            <CenteredNote text="Map unavailable." />
          ) : loadError ? (
            <CenteredNote text="Failed to load map." />
          ) : !isLoaded ? (
            <CenteredNote spinner />
          ) : (
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={US_CENTER}
              zoom={US_DEFAULT_ZOOM}
              onLoad={onMapLoad}
              onUnmount={onMapUnmount}
              options={{
                mapId: GOOGLE_MAP_ID,
                disableDefaultUI: false,
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: false,
                clickableIcons: false,
              }}
            />
          )}

          <Box sx={{
            position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
            display: 'flex', alignItems: 'center', gap: 1, maxWidth: '92%',
          }}>
            {busy && (
              <Box sx={{
                bgcolor: '#fff', borderRadius: '999px', px: 2, py: 0.75,
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)', display: 'flex', alignItems: 'center', gap: 1,
              }}>
                <CircularProgress size={16} sx={{ color: theme.primary }} />
                {loadingMore && !loading && (
                  <Typography sx={{ fontSize: 12, color: '#334155', fontWeight: 600 }} noWrap>
                    Loading Mobilize events…
                  </Typography>
                )}
              </Box>
            )}

            {!busy && showSearchArea && isLoaded && (
              <Button
                onClick={handleSearchThisArea}
                startIcon={<SearchIcon sx={{ fontSize: 18 }} />}
                sx={{
                  bgcolor: '#fff', color: '#0f172a', fontWeight: 700, fontSize: 13,
                  textTransform: 'none', borderRadius: '999px', px: 2, py: 0.85,
                  boxShadow: '0 2px 10px rgba(0,0,0,0.18)', minHeight: 44,
                  '&:hover': { bgcolor: '#f1f5f9' },
                }}
              >
                Search this area
              </Button>
            )}

            {!busy && !showSearchArea && externalPartial && (
              <Box sx={{
                bgcolor: '#fff', borderRadius: '999px', pl: 2, pr: 0.75, py: 0.5,
                boxShadow: '0 2px 8px rgba(0,0,0,0.14)', display: 'flex', alignItems: 'center', gap: 0.75,
              }}>
                <Typography sx={{ fontSize: 12, color: '#334155', fontWeight: 600 }} noWrap>
                  Some Mobilize events are still loading
                </Typography>
                <Button
                  onClick={loadEvents}
                  sx={{
                    minWidth: 0, minHeight: 36, px: 1.25, fontSize: 12, fontWeight: 700,
                    textTransform: 'none', color: theme.primary,
                  }}
                >
                  Retry
                </Button>
              </Box>
            )}
          </Box>

          {isLoaded && (
            <Tooltip title="Refresh events">
              <span style={{ position: 'absolute', top: 12, right: 12 }}>
                <IconButton
                  onClick={loadEvents}
                  disabled={busy}
                  aria-label="Refresh events"
                  sx={{
                    bgcolor: '#fff', width: 44, height: 44,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.16)', color: '#0f172a',
                    '&:hover': { bgcolor: '#f1f5f9' },
                    '&.Mui-disabled': { bgcolor: '#fff', color: '#94a3b8' },
                  }}
                >
                  <RefreshIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {isLoaded && !busy && !error && mappableEvents.length === 0 && (
            <Box sx={{
              position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)',
              bgcolor: '#fff', borderRadius: '12px', px: 2, py: 1.25,
              boxShadow: '0 2px 8px rgba(0,0,0,0.14)', maxWidth: '90%',
            }}>
              <Typography sx={{ fontSize: 13, color: '#0f172a', fontWeight: 600, textAlign: 'center' }}>
                {searchBounds
                  ? 'No events with a location in this area. Zoom out and search again.'
                  : 'No events with a map location.'}
              </Typography>
            </Box>
          )}
        </Box>

        {error && (
          <Box sx={{ textAlign: 'center', py: 3, color: '#dc2626' }}>
            <Typography sx={{ mb: 1.5 }}>{error}</Typography>
            <Button onClick={loadEvents} variant="contained" sx={{ bgcolor: theme.primary, '&:hover': { bgcolor: theme.primaryHover } }}>
              Retry
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
}

function CenteredNote({ text, spinner }) {
  return (
    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1.5 }}>
      {spinner && <CircularProgress size={36} sx={{ color: theme.primary }} />}
      {text && <Typography sx={{ fontSize: 14, color: '#475569', fontWeight: 600, textAlign: 'center', px: 3 }}>{text}</Typography>}
    </Box>
  );
}
